import {isEmpty} from 'lodash-es';
import React, {useState} from 'react';

import {
  AfriexAdminPermission,
  AfriexFeatureCode,
} from '@afriex/core/domain/enums';

import {
  Button,
  CheckBox,
  ConfirmationModal,
  Input,
  Select,
  SpinningLoader,
} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {
  AfriexFeature,
  AfriexFeatureField,
  AfriexFeatureLimit,
  AfriexFeatureTagMap,
} from '@/types';
import {logRejection} from '@/utils/logRejection';

import {FeaturePredicateInput} from './FeaturePredicateInput';
import LimitInput from './LimitInput';
import {FeatureActionType} from './types';
import useFeatureUpdate from './useFeatureUpdate';

/** The only codes a feature may use, so a typo cannot silently disable it. */
const codeOptions = Object.values(AfriexFeatureCode).map((value) => ({
  key: value,
  value,
}));

interface FeatureFormModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  initialData?: AfriexFeature;
}

const FeatureFormModal = ({
  isOpen,
  onClose,
  initialData,
}: FeatureFormModalProps) => {
  const {dispatch, isLoading, handleSubmit, handleDelete, feature} =
    useFeatureUpdate(onClose, initialData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {code, name, meta} = feature;
  const flags = meta?.flags ?? {};
  const updateLabel = isEmpty(initialData)
    ? 'Create New Feature +'
    : 'Update Feature';
  const submitLabel = isEmpty(initialData) ? 'Submit' : 'Update';
  // A feature's code is its identity, so an existing one cannot be renamed;
  // it is only chosen when creating.
  const isUpdating = !isEmpty(initialData);
  // An existing feature can predate the enum. Offer its own code too, or the
  // locked select would silently display the first enum option instead.
  const isLegacyCode =
    isUpdating &&
    !!code &&
    !Object.values<string>(AfriexFeatureCode).includes(code);
  const selectableCodeOptions = isLegacyCode
    ? [...codeOptions, {key: code, value: code}]
    : codeOptions;

  const isDisabled =
    isEmpty(feature?.name) || isEmpty(feature?.code) || isLoading;

  const handleConfirmDelete = async () => {
    await handleDelete(String(feature.id));
    setShowDeleteConfirm(false);
  };
  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
        closeOnOutsideClick
        title={updateLabel}
        dataCy="merchant-features-form"
      >
        <div className="pt-3 px-2 overflow-hidden">
          {isLoading ? (
            <div className="flex w-full h-full justify-center items-center my-5">
              <SpinningLoader />
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // `handleSubmit` toasts its own failures, so this only fires if
                // that handler itself throws. Log rather than swallow silently.
                handleSubmit(feature).catch(
                  logRejection('Failed to submit feature'),
                );
              }}
              className="flex flex-col"
            >
              <Input
                label="Name"
                placeholder="Name of Feature"
                value={name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: FeatureActionType.UPDATE_FEATURE,
                    payload: {
                      name: e.target?.value,
                    },
                  })
                }
                className="w-full mb-2"
                dataCy="merchant-features-form-name"
              />

              <Select
                label="Code"
                dataCy="merchant-features-form-code"
                placeholder="Select a code"
                options={selectableCodeOptions}
                value={code ?? ''}
                aria-disabled={isUpdating}
                tabIndex={isUpdating ? -1 : undefined}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  if (isUpdating) {
                    return;
                  }
                  dispatch({
                    type: FeatureActionType.UPDATE_FEATURE,
                    payload: {
                      code: e.target?.value,
                    },
                  });
                }}
                className={`w-full mb-2${isUpdating ? ' pointer-events-none' : ''}`}
              />

              <div className="mb-2">
                <div className="w-full mb-2">
                  <FeaturePredicateInput
                    tags={meta?.tags}
                    onRemove={(tag: AfriexFeatureField) =>
                      dispatch({
                        type: FeatureActionType.REMOVE_TAG,
                        payload: tag,
                      })
                    }
                    onChange={(tag: AfriexFeatureTagMap) =>
                      dispatch({
                        type: FeatureActionType.ADD_TAG,
                        payload: tag,
                      })
                    }
                    fieldsToOmit={[]}
                  />
                </div>
              </div>

              <LimitInput
                label="Limit"
                dataCy="merchant-features-form-limit"
                value={meta?.limit}
                onChange={(limit: AfriexFeatureLimit) =>
                  dispatch({
                    type: FeatureActionType.UPDATE_LIMIT,
                    payload: limit,
                  })
                }
              />
              <hr className="my-4 dark:border-dark-borderMid" />

              <CheckBox
                label="Active"
                dataCy="merchant-features-form-active"
                type="checkbox"
                checked={Boolean(!flags?.isDeactivated)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: FeatureActionType.UPDATE_FEATURE,
                    payload: {
                      meta: {
                        ...feature.meta,
                        flags: {
                          ...flags,
                          isDeactivated: !e.target?.checked,
                        },
                      },
                    },
                  })
                }
              />

              <div className="flex items-center justify-end pb-5">
                {isUpdating && (
                  <PermissionsProvider
                    permission={AfriexAdminPermission.FeatureManage}
                  >
                    <Button
                      variant="outline"
                      colorScheme="red"
                      className="mr-auto"
                      onClick={() => setShowDeleteConfirm(true)}
                      dataCy="merchant-features-form-delete"
                    >
                      Delete
                    </Button>
                  </PermissionsProvider>
                )}
                <Button
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                  dataCy="merchant-features-form-submit"
                >
                  {submitLabel}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Feature"
        label={`Delete feature ${name ?? ''}? This cannot be undone.`}
        confirmActionLabel="Delete"
        cancelActionLabel="Cancel"
        loadingLabel="Deleting..."
        loading={isLoading}
        confirmAction={handleConfirmDelete}
        cancelAction={() => setShowDeleteConfirm(false)}
        dataCy="merchant-features-delete-confirmation"
      />
    </React.Fragment>
  );
};

export default FeatureFormModal;
